import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { EstablishmentService } from 'src/app/main/pages/services/establishment.service';
import { FormValidator } from 'src/app/shared/utils/form-validator';
import { LoadingComponent } from 'src/app/main/pages/generals/loading/loading.component';

import { BaseDialogComponent } from '../../../../../generals/dialog/base-dialog/base-dialog.component';
import { EstablishmentSimpleJson } from '../../../../../services/microservices/json/establishment-simple.json';

@Component({
  selector: 'establishment-dialog',
  templateUrl: './establishment-dialog.component.html',
  styleUrls: ['./establishment-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    LoadingComponent,
    BaseDialogComponent,
  ],
})
export class EstablishmentDialogComponent implements OnInit, OnDestroy {
  @Output() openModel = new EventEmitter();

  readonly dialogRef = inject(MatDialogRef<EstablishmentDialogComponent>);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private _unsubscribeAll!: Subject<any>;
  private _snackBar = inject(MatSnackBar);
  private service = inject(EstablishmentService);

  public data: { id: number; signatureId: number } = inject(MAT_DIALOG_DATA);
  txtBtn = 'REGISTRAR';
  isLoading = false;
  notification: any = {};
  form!: FormGroup;
  image!: string;
  file!: File;
  formSubmitted = false;

  constructor(private _dialog: MatDialog) {}
  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.formSubmitted = false;
    this.formInit();
    this.load();
  }

  openDialog() {
    this._dialog.open(BaseDialogComponent, {
      width: '355px',
      height: '355px',
      data: { name: 'name' },
      disableClose: false,
    });

    this.openModel.emit();
  }

  private load(): void {
    if (this.data.signatureId <= 0) {
      this.form.disable();
      return;
    }

    this.form.patchValue({ signatureId: this.data.signatureId });

    if (this.data.id <= 0) {
      return;
    }

    this.get();
  }
  get(): void {
    this.isLoading = true;
    this.formSubmitted = false;
    this.service.get(this.data.id).subscribe({
      next: response => {
        this.form.patchValue(response.data);
        this.showBase46Image(response.data.image, response.data.contentType);
        this.setFile(
          response.data.image,
          response.data.imageName,
          response.data.contentType,
        );
        this.txtBtn = 'ACTUALIZAR';
        this.isLoading = false;
      },
    });
  }
  submit(): void {
    this.formSubmitted = true;
    this.form.markAllAsTouched();

    // Verificamos que ambos campos sean válidos antes de enviar
    if (!this.areAllFieldsValid()) {
      if (!this.file || !this.image || this.image === '') {
        this.notification.image = 'La imagen del establecimiento es requerida';
      }

      if (!this.form.get('tradeName')?.valid) {
        // El error ya está configurado por el validador de Angular
      }

      this.fv.invalidFormHasSubmit();
      return;
    }

    this.isLoading = true;
    const formData = new FormData();
    const currentFormValue = { ...this.form.value };
    const formValueForJson = { ...currentFormValue, image: '' };
    const data = JSON.stringify(formValueForJson);

    formData.append('data', new Blob([data], { type: 'application/json' }));
    if (this.file) {
      formData.append('file', this.file);
    }

    if (this.form.get('id')?.value > 0) {
      this.update(formData);
      return;
    }

    this.save(formData);
  }

  save(data: FormData): void {
    this.service.save(data).subscribe({
      next: response => {
        this.dialogRef.close(
          this.getJsonResult(response.data.id, response.data.number),
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  update(data: FormData): void {
    this.service.update(data).subscribe({
      next: response => {
        this.dialogRef.close(
          this.getJsonResult(response.data.id, response.data.number),
        );
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
      },
    });
  }
  onFileSelected(event: any): void {
    const file: File = event.target?.files.item(0);
    if (!file) {
      return;
    }

    this.file = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.image = reader.result as string;
      this.form
        .get('image')
        ?.setValue(this.image, { emitEvent: false, onlySelf: true });
      this.form.get('image')?.markAsTouched({ onlySelf: true });
    };
    reader.readAsDataURL(file);
  }
  private showBase46Image(base64: string, contentType: string): void {
    this.image = `data:${contentType};base64,${base64}`;
    this.form.get('image')?.setValue(this.image, { emitEvent: false });
  }

  private setFile(
    base64: string,
    imageName: string,
    contentType: string,
  ): void {
    const byteArray = new Uint8Array(
      atob(base64)
        .split('')
        .map(char => char.charCodeAt(0)),
    );
    const data = new Blob([byteArray], { type: contentType });
    const arrayOfBlob = new Array<Blob>();
    arrayOfBlob.push(data);
    this.file = new File(arrayOfBlob, imageName, { type: contentType });
  }
  private formInit(): void {
    this.form = this.fb.group(
      {
        id: [0],
        tradeName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(100),
          ],
        ],
        image: [''],
        imageName: '',
        contentType: '',
        signatureId: 0,
      },
      { updateOn: 'change' },
    );

    Object.defineProperty(this.form, 'submitted', {
      value: false,
      writable: true,
    });

    this.fieldNotificationInit();
  }
  private fieldNotificationInit(): void {
    const fields = {
      tradeName: {
        required: 'Nombre comercial es requerido',
        minlength: 'El nombre comercial debe tener al menos 2 caracteres',
        maxlength: 'El nombre comercial no debe exceder los 100 caracteres',
      },
      image: { required: 'La imagen del establecimiento es requerida' },
    };
    this.fv.setFormValidator(this.form, this.notification, fields);
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  private getJsonResult(id: number, number: number): EstablishmentSimpleJson {
    return {
      id,
      tradeName: this.form.get('tradeName')?.value,
      number,
      branchesOffice: [],
    };
  }
  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }
  imageHasError(): boolean {
    if (!this.form) {
      return false;
    }

    const control = this.form.get('image');
    if (this.image && this.image !== '' && this.file) {
      return false;
    }
    return (
      control?.invalid === true &&
      (this.formSubmitted === true || control?.touched === true)
    );
  }
  tradeNameHasError(): boolean {
    if (!this.form) {
      return false;
    }

    const control = this.form.get('tradeName');
    return (
      control?.invalid === true &&
      (this.formSubmitted === true ||
        (control?.touched === true && control?.dirty === true))
    );
  }

  // Método para validar si ambos campos son válidos para habilitar el botón
  areAllFieldsValid(): boolean {
    if (!this.form) {
      return false;
    }

    // Verifica si el nombre comercial es válido
    const tradeNameControl = this.form.get('tradeName');
    const isTradeNameValid =
      tradeNameControl?.valid === true &&
      tradeNameControl?.value &&
      tradeNameControl?.value.trim().length >= 2;

    // Verifica si la imagen es válida
    const hasValidImage = !!(this.image && this.image !== '' && this.file);

    // Solo devuelve true si ambos son válidos
    return isTradeNameValid && hasValidImage;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }
}
